import React, { useEffect, useState, useRef } from 'react';
// React es el núcleo del componente. Se importan hooks como useEffect, useState y useRef:
// - `useEffect`: Para manejar efectos secundarios (como API calls o eventos).
// - `useState`: Para manejar estados locales como `moonPhases` o `hemisphere`.
// - `useRef`: Para interactuar directamente con el DOM (en este caso, el calendario).

import FullCalendar from '@fullcalendar/react';
// FullCalendar es el componente principal que renderiza el calendario interactivo.

import dayGridPlugin from '@fullcalendar/daygrid';
// Plugin de FullCalendar que permite mostrar eventos en formato de cuadrícula mensual.

import listPlugin from '@fullcalendar/list';
// Plugin que habilita la vista de lista para mostrar eventos en formato vertical.

import rrulePlugin from '@fullcalendar/rrule';
// Plugin para manejar reglas de recurrencia (eventos que se repiten).

import interactionPlugin from '@fullcalendar/interaction';
// Plugin que habilita interacciones como clics y arrastrar/soltar eventos.

import events from './calendarEvents';
// Archivo local que contiene eventos predefinidos para mostrar en el calendario.

import { generateMoonPhases } from './generateMoonPhases';
// Función personalizada que genera eventos basados en las fases lunares.

import esLocale from '@fullcalendar/core/locales/es';
// Configuración de idioma para FullCalendar (en este caso, español).

const CannabisCalendar = () => {
  // Estado para almacenar las fases lunares generadas dinámicamente.
  const [moonPhases, setMoonPhases] = useState([]);

  // Estado para almacenar el hemisferio seleccionado (norte o sur).
  const [hemisphere, setHemisphere] = useState('south');

  // Estado para el mensaje diario, que se muestra basado en el evento actual.
  const [todayMessage, setTodayMessage] = useState('');

  // Referencia al calendario para controlarlo programáticamente.
  const calendarRef = useRef(null);

  /**
   * Genera las fases lunares para el año actual y el siguiente
   * basándose en el hemisferio seleccionado.
   * Este efecto se ejecuta cada vez que cambia el hemisferio.
   */
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const phases = [
      ...generateMoonPhases(currentYear, hemisphere),
      ...generateMoonPhases(currentYear + 1, hemisphere),
    ];
    setMoonPhases(phases); // Actualiza el estado con las fases generadas.
  }, [hemisphere]);

  /**
   * Actualiza el mensaje diario basado en la fecha actual y
   * los eventos generados. Si no hay un evento, muestra un
   * mensaje genérico.
   */
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]; // Fecha actual en formato ISO.
    const todaysEvents = [...events, ...moonPhases].filter(event => event.date === today); // Busca los evento de hoy.
   
    const eventsTitles = todaysEvents.map(event => event.title).join(', ');
    if (todaysEvents.length > 0) {
      setTodayMessage(`HOY: ${eventsTitles}`);
    } else {
      setTodayMessage('Día para admirarlas y decirles cosas lindas.');
    }
  }, [moonPhases]);

  /**
   * Cambia la vista del calendario según el tamaño de la ventana.
   * - Pantallas pequeñas (<768px): Muestra el modo lista.
   * - Pantallas grandes: Muestra la cuadrícula del mes.
   */
  useEffect(() => {
    const handleResize = () => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const newView = window.innerWidth < 768 ? 'listMonth' : 'dayGridMonth';
        if (calendarApi.view.type !== newView) {
          calendarApi.changeView(newView); // Cambia la vista si es necesario.
        }
      }
    };

    window.addEventListener('resize', handleResize); // Escucha cambios en el tamaño de la ventana.
    handleResize(); // Ejecuta la lógica al cargar el componente.

    return () => {
      window.removeEventListener('resize', handleResize); // Limpia el evento al desmontar el componente.
    };
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 bg-gray-800 text-white rounded-lg shadow-lg">
      {/* Título principal del calendario */}
      <h1 className="text-3xl md:text-4xl font-extrabold mb-4 text-center text-green-400">
        Calendario Lunar de Cultivo
      </h1>

      {/* Botones para seleccionar el hemisferio */}
      <div className="text-center mb-4">
        <button
          className={`mx-1 md:mx-2 px-3 md:px-4 py-1 md:py-2 rounded ${
            hemisphere === 'north' ? 'bg-blue-500' : 'bg-gray-700'
          }`}
          onClick={() => setHemisphere('north')} // Cambia al hemisferio norte.
        >
          Hemisferio Norte
        </button>
        <button
          className={`mx-1 md:mx-2 px-3 md:px-4 py-1 md:py-2 rounded ${
            hemisphere === 'south' ? 'bg-blue-500' : 'bg-gray-700'
          }`}
          onClick={() => setHemisphere('south')} // Cambia al hemisferio sur.
        >
          Hemisferio Sur
        </button>
      </div>

      {/* Mensaje del día basado en el evento actual */}
      <div className="text-center mb-4">
        <p className="text-lg md:text-xl font-bold">{todayMessage}</p>
      </div>

      {/* Calendario interactivo */}
      <div className="bg-black text-green-400 rounded-lg overflow-x-auto">
      <FullCalendar
  ref={calendarRef}
  plugins={[dayGridPlugin, listPlugin, interactionPlugin, rrulePlugin]}
  initialView="dayGridMonth"
  initialDate={new Date().toISOString().split('T')[0]} // Establece la fecha inicial en el día actual
  events={[...events, ...moonPhases]}
  locales={[esLocale]}
  locale="es"
  titleFormat={{
    year: 'numeric', // Año completo
    month: 'short',   // Nombre completo del mes

  }}
  headerToolbar={{
    left: 'prev,next today',
    center: 'title',
    right: 'dayGridMonth,listMonth',
  }}
  views={{
    listMonth: {
      buttonText: 'Lista',
      allDayText: '', // Quita el texto "todo el día".
    },
  }}
  height="auto"
  dayMaxEventRows={true}
  eventContent={(arg) => (
    <div className="whitespace-normal break-words text-xs md:text-sm text-center p-2 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-lg shadow-md">
      {arg.event.title} {/* Renderiza el título del evento */}
    </div>
  )}
  dayHeaderClassNames="bg-black text-green text-sm uppercase font-semibold tracking-wide"
  dayCellClassNames="border border-gray-600 hover:bg-green-200 transition-colors duration-200 ease-in-out"
  eventClassNames="hover:scale-105 transform transition-transform duration-200 ease-in-out"

 
/>
      </div>
    </div>
  );
};

export default CannabisCalendar;
